import { get, set, del, keys, clear } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

/**
 * IndexedDB-based persister for React Query.
 * Stores cached query data for offline access.
 */
const CACHE_KEY = 'stoneflow-query-cache';

export const queryPersister: Persister = {
  persistClient: async (client: PersistedClient) => {
    await set(CACHE_KEY, client);
  },
  restoreClient: async () => {
    return await get<PersistedClient>(CACHE_KEY);
  },
  removeClient: async () => {
    await del(CACHE_KEY);
  },
};

/**
 * Storage utility functions for managing IndexedDB data.
 */
export async function getStorageEstimate(): Promise<{ used: number; available: number }> {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    return {
      used: estimate.usage || 0,
      available: estimate.quota || 0,
    };
  }
  // Fallback: assume 50MB available
  return { used: 0, available: 50 * 1024 * 1024 };
}

export async function checkStorageHealth(): Promise<{
  usagePercent: number;
  shouldCleanup: boolean;
}> {
  const { used, available } = await getStorageEstimate();
  const usagePercent = available > 0 ? (used / available) * 100 : 0;
  
  return {
    usagePercent,
    shouldCleanup: usagePercent > 80,
  };
}

/**
 * Clear all offline data (for debugging/reset).
 */
export async function clearAllOfflineData(): Promise<void> {
  await clear();
  console.log('[Offline] All offline data cleared');
}

/**
 * Get all keys in IndexedDB (for debugging).
 */
export async function getAllStorageKeys(): Promise<IDBValidKey[]> {
  return await keys();
}
